import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Car, Search, Loader2, Circle, CircleDot, Download, Eye, Pencil, Trash2 } from 'lucide-react';
import { convertToCSV, downloadCSV, formatDateForCSV } from '@/utils/csvExport';
import { downloadDocument, viewSignedDocument, downloadSignedDocument } from '@/utils/fileDownload';

const Vehicles = () => {
  const { API } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    vehicle_number: '',
    model: '',
    make: '',
    chassis_number: '',
    engine_number: ''
  });
  const [files, setFiles] = useState({
    rc_book: null,
    insurance: null,
    puc: null,
    permit: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const handleViewVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setViewDialogOpen(true);
  };

  const handleDeleteVehicle = async (vehicle) => {
    if (!window.confirm(`Permanently delete vehicle ${vehicle.vehicle_number}?\n\nThis cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/vehicles/${vehicle.id}`);
      toast.success('Vehicle deleted');
      fetchVehicles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete vehicle');
    }
  };

  const handleEditVehicleNumber = async (vehicle) => {
    const newNum = window.prompt(`Edit vehicle number (current: ${vehicle.vehicle_number}):`, vehicle.vehicle_number);
    if (!newNum || newNum === vehicle.vehicle_number) return;
    try {
      await axios.patch(`${API}/vehicles/${vehicle.id}`, { vehicle_number: newNum });
      toast.success('Vehicle number updated');
      fetchVehicles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update vehicle number');
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles`, {
        params: search ? { search } : {}
      });
      setVehicles(response.data);
    } catch (error) {
      toast.error('Failed to fetch vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setLoading(true);
    fetchVehicles();
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileChange = (type, e) => {
    const file = e.target.files[0];
    if (file) {
      setFiles({ ...files, [type]: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Create vehicle
      const response = await axios.post(`${API}/vehicles`, formData);
      const vehicleId = response.data.id;

      // Upload files
      for (const [type, file] of Object.entries(files)) {
        if (file) {
          const base64 = await fileToBase64(file);
          await axios.post(`${API}/vehicles/${vehicleId}/upload`, {
            file_name: file.name,
            file_data: base64,
            document_type: type
          });
        }
      }

      toast.success('Vehicle onboarded successfully');
      setDialogOpen(false);
      setFormData({ vehicle_number: '', model: '', make: '', chassis_number: '', engine_number: '' });
      setFiles({ rc_book: null, insurance: null, puc: null, permit: null });
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to onboard vehicle');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportCSV = () => {
    const headers = [
      { label: 'Vehicle ID', key: 'vehicle_id' },
      { label: 'Vehicle Number', key: 'vehicle_number' },
      { label: 'Make', key: 'make' },
      { label: 'Model', key: 'model' },
      { label: 'Status', key: 'is_allotted' },
      { label: 'Created At', key: 'created_at' }
    ];

    const csvData = vehicles.map(vehicle => ({
      ...vehicle,
      is_allotted: vehicle.is_allotted ? 'Allotted' : 'Available',
      created_at: formatDateForCSV(vehicle.created_at)
    }));

    const csv = convertToCSV(csvData, headers);
    const filename = `vehicles_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csv, filename);
    toast.success('Vehicles data exported successfully');
  };

  return (
    <div className="space-y-6" data-testid="vehicles-section">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Vehicle Management</CardTitle>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Search vehicles..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-64 input-focus"
                  data-testid="vehicle-search-input"
                />
                <Button onClick={handleSearch} variant="outline" data-testid="search-btn">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
              <Button
                variant="outline"
                onClick={handleExportCSV}
                disabled={vehicles.length === 0}
                data-testid="export-vehicles-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button
                className="btn-primary"
                onClick={() => setDialogOpen(true)}
                data-testid="new-vehicle-btn"
              >
                <Car className="w-4 h-4 mr-2" />
                Add New Vehicle
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <Table data-testid="vehicles-table">
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Vehicle ID</TableHead>
                  <TableHead>Vehicle Number</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Make</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vehicles.map((vehicle) => (
                  <TableRow key={vehicle.id} className="table-row">
                    <TableCell className="font-mono">{vehicle.vehicle_id}</TableCell>
                    <TableCell className="font-mono font-semibold">{vehicle.vehicle_number}</TableCell>
                    <TableCell>{vehicle.model}</TableCell>
                    <TableCell>{vehicle.make}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {vehicle.is_allotted ? (
                          <>
                            <CircleDot className="w-4 h-4 text-orange-500" />
                            <span className="status-badge bg-orange-100 text-orange-700">Allotted</span>
                          </>
                        ) : (
                          <>
                            <Circle className="w-4 h-4 text-green-500" />
                            <span className="status-badge status-active">Available</span>
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewVehicle(vehicle)}
                          data-testid={`view-vehicle-btn-${vehicle.id}`}
                          title="View documents"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditVehicleNumber(vehicle)}
                          data-testid={`edit-vehicle-num-${vehicle.id}`}
                          title="Edit vehicle number"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteVehicle(vehicle)}
                          data-testid={`delete-vehicle-${vehicle.id}`}
                          title="Delete permanently"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {vehicles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      No vehicles found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Vehicle Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="vehicle-form-dialog">
          <DialogHeader>
            <DialogTitle>Add New Vehicle</DialogTitle>
            <DialogDescription>Fill in vehicle details and upload required documents</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2 col-span-3">
                <Label htmlFor="vehicle_number">Vehicle Number *</Label>
                <Input
                  id="vehicle_number"
                  value={formData.vehicle_number}
                  onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value.toUpperCase() })}
                  placeholder="MH01AB1234"
                  required
                  data-testid="vehicle-number-input"
                  className="input-focus font-mono"
                />
              </div>
              <div className="space-y-2 col-span-3 sm:col-span-1">
                <Label htmlFor="make">Make *</Label>
                <Input
                  id="make"
                  value={formData.make}
                  onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                  placeholder="Tata, Mahindra, etc."
                  required
                  data-testid="vehicle-make-input"
                  className="input-focus"
                />
              </div>
              <div className="space-y-2 col-span-3 sm:col-span-2">
                <Label htmlFor="model">Model *</Label>
                <Input
                  id="model"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  placeholder="Ace, Bolero, etc."
                  required
                  data-testid="vehicle-model-input"
                  className="input-focus"
                />
              </div>
              <div className="space-y-2 col-span-3 sm:col-span-2">
                <Label htmlFor="chassis_number">Chassis No</Label>
                <Input
                  id="chassis_number"
                  value={formData.chassis_number}
                  onChange={(e) => setFormData({ ...formData, chassis_number: e.target.value.toUpperCase() })}
                  placeholder="MA3JMTB1STBE41638"
                  data-testid="vehicle-chassis-input"
                  className="input-focus font-mono"
                />
              </div>
              <div className="space-y-2 col-span-3 sm:col-span-1">
                <Label htmlFor="engine_number">Engine No</Label>
                <Input
                  id="engine_number"
                  value={formData.engine_number}
                  onChange={(e) => setFormData({ ...formData, engine_number: e.target.value.toUpperCase() })}
                  placeholder="K10CNC974538"
                  data-testid="vehicle-engine-input"
                  className="input-focus font-mono"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold mb-4">Upload Documents</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rc_book">RC Book</Label>
                  <Input
                    id="rc_book"
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => handleFileChange('rc_book', e)}
                    data-testid="rc-book-upload"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="insurance">Insurance</Label>
                  <Input
                    id="insurance"
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => handleFileChange('insurance', e)}
                    data-testid="insurance-upload"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="puc">PUC</Label>
                  <Input
                    id="puc"
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => handleFileChange('puc', e)}
                    data-testid="puc-upload"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="permit">Permit</Label>
                  <Input
                    id="permit"
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => handleFileChange('permit', e)}
                    data-testid="permit-upload"
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full btn-primary"
              disabled={submitting}
              data-testid="submit-vehicle-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Vehicle
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Vehicle Documents Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="vehicle-view-dialog">
          <DialogHeader>
            <DialogTitle>Vehicle Details</DialogTitle>
            <DialogDescription>View and download uploaded vehicle documents</DialogDescription>
          </DialogHeader>
          {selectedVehicle && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Vehicle ID</Label>
                  <p className="font-mono font-semibold">{selectedVehicle.vehicle_id}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Vehicle Number</Label>
                  <p className="font-mono font-semibold">{selectedVehicle.vehicle_number}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Make</Label>
                  <p>{selectedVehicle.make}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Model</Label>
                  <p>{selectedVehicle.model}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Uploaded Documents</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {[
                    { key: 'rc_book', label: 'RC Book' },
                    { key: 'insurance', label: 'Insurance' },
                    { key: 'puc', label: 'PUC' },
                    { key: 'permit', label: 'Permit' },
                  ].map((doc) => {
                    const url = selectedVehicle[doc.key];
                    const filename = `${selectedVehicle.vehicle_number}_${doc.key}`;
                    const endpoint = `/vehicles/${selectedVehicle.id}/file-url/${doc.key}`;
                    return (
                      <div
                        key={doc.key}
                        className="flex items-center justify-between gap-3 px-3 py-2 border rounded-md bg-gray-50"
                        data-testid={`vehicle-doc-row-${doc.key}`}
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${url ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                          <span className="text-sm font-medium truncate">{doc.label}</span>
                        </div>
                        {url ? (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => viewSignedDocument(API, endpoint)}
                              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline px-2 py-1 rounded"
                              data-testid={`vehicle-view-${doc.key}`}
                            >
                              <Eye className="w-3 h-3" />
                              View
                            </button>
                            <button
                              type="button"
                              onClick={() => downloadSignedDocument(API, endpoint, filename)}
                              className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 hover:underline px-2 py-1 rounded"
                              data-testid={`vehicle-download-${doc.key}`}
                            >
                              <Download className="w-3 h-3" />
                              Download
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 flex-shrink-0">Not uploaded</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Vehicles;
